/* eslint-disable */
import React, { useEffect, lazy, Suspense, useState, useCallback } from 'react';
import { ThemeProvider } from '@mtnkente/cl-super-app-foundation';
import { useWebViewMessageListener, sendJsonToNative } from './hooks/UseWebViewMessageListener';
import { CoreFonts } from '@mtnkente/paragon-core-fonts';
import { Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { NavigationServiceV1 } from './services/NavigationService';
import { useThemeContext, ThemeContextProvider } from './context/ThemeContext';
import { MyListProvider } from './context/MyListContext';
import { useDataShareStore } from './hooks/DataSharingContext';
import { isLocalEnvironment } from '../utils/environment';
import { AppSpecificConfigs, WebViewMessage } from '../types/read-feed.types';
import i18n from '../localization/internationalization';
import Loader from './components/loader/Loader';
import { callNavigation } from '../utils/helper';
import { AuthProvider } from './context/AuthContext';
import SignIn from '../auth/SignIn';
import { Auth0Provider } from '@auth0/auth0-react';
import { useTranslationService } from '../localization/provider/TranslationServiceProvider';
import { Env, useEnv } from './hooks/use-env';

// Lazy load components
const Feeds = lazy(() => import('./components/feeds/ExploreFeed'));
const OverviewWidget = lazy(() => import('./components/overview/OverviewWidget'));
const ContentDetails = lazy(() => import('./components/content-details/ContentDetailsRefactored'));

type AppProps = {
  config?: unknown;
  navigationService: NavigationServiceV1;
};

const queryClient = new QueryClient();

const AppContent: React.FC<AppProps> = ({ navigationService }) => {
  const [configs, setConfigs] = useState<AppSpecificConfigs>();
  const [isAppLoading, setIsAppLoading] = useState<boolean>(true);
  const { ready: translationsReady } = useTranslationService();
  const { theme } = useThemeContext();
  const { setData } = useDataShareStore();

  useEffect(() => {
    sendJsonToNative(JSON.stringify({ action: 'get_configs' }));
  }, []);

  const receivedData = useWebViewMessageListener() as WebViewMessage | null;
  const { initialized } = useTranslationService();

  useEffect(() => {
    if (!receivedData) return;

    switch (receivedData.type) {
      case 'get_configs':
        localStorage.setItem('configs', JSON.stringify(receivedData.payload));
        setConfigs(receivedData.payload as AppSpecificConfigs);

        const loadTranslation = async (): Promise<void> => {
          try {
            if (receivedData.payload?.languageTag) {
              await i18n.changeLanguage(receivedData.payload.languageTag);
            }
          } catch (err) {
            console.error('Language load error', err);
          }
        };

        loadTranslation();
        break;
      default:
        if (process.env.NODE_ENV === 'development') {
          console.debug('Ignored message from WebView:', receivedData.type);
        }
        break;
    }
  }, [receivedData, setData]);

  useEffect(() => {
    if (translationsReady) {
      setIsAppLoading(false);
    }
  }, [translationsReady]);

  const navigateToHome = useCallback((): void => {
    callNavigation('/NavigateBack');
  }, []);

  const refreshApp = useCallback(() => {
    sendJsonToNative(JSON.stringify({ action: 'get_configs' }));
    setIsAppLoading(true);
  }, []);

  const env = useEnv();

  // Detecting platform
  if (!env) {
    return (
      <ThemeProvider mode={theme} platform='core'>
        <CoreFonts />
        <Loader message='' />
      </ThemeProvider>
    );
  }

  if (env === Env.BROWSER) {
    return (
      <ThemeProvider mode={theme} platform='core'>
        <CoreFonts />
        <Auth0Provider
          domain={process.env.REACT_APP_OKTA_DOMAIN ?? ''}
          clientId={process.env.REACT_APP_OKTA_CLIENT_ID ?? ''}
          authorizationParams={{
            redirectUri: process.env.REACT_APP_OKTA_REDIRECT_URI ?? window.location.origin,
          }}
        >
          <SignIn />
        </Auth0Provider>
      </ThemeProvider>
    );
  }

  if (isAppLoading && !initialized && env === Env.WEBVIEW) {
    return (
      <ThemeProvider mode={theme} platform='core'>
        <CoreFonts />
        <Loader
          isLoading={isAppLoading}
          isReady={translationsReady}
          error={!configs?.opco}
          handleHomeClick={navigateToHome}
          handleRefreshClick={refreshApp}
        />
      </ThemeProvider>
    );
  }

  const initialUserData = {
    msisdn: isLocalEnvironment() ? `${process.env.DEV_MSISDN}` : configs?.msisdn,
    opco: configs?.opco ?? (JSON.parse(localStorage.getItem('configs') || '{}')?.opco as string),
    language:
      configs?.languageTag ??
      (JSON.parse(localStorage.getItem('configs') ?? '{}')?.languageTag as string),
    accessToken: `${process.env.SATURN_BFF_TOKEN}`,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider initialUserData={{ ...initialUserData, msisdn: initialUserData.msisdn ?? '' }}>
        <MyListProvider>
          <ThemeProvider mode={configs?.theme ?? theme} platform='core'>
            <CoreFonts />
            <Suspense fallback={<Loader isLoading={!configs?.opco} />}>
              <Routes>
                <Route
                  path='/read-content'
                  element={
                    <ContentDetails
                      isReady={translationsReady}
                      navigationService={navigationService}
                    />
                  }
                />
                <Route
                  path='/read-overview'
                  element={
                    <OverviewWidget
                      opco={initialUserData.opco}
                      selectedUserLanguage={initialUserData.language}
                      isReady={translationsReady}
                    />
                  }
                />
                <Route
                  path='/read'
                  element={
                    <Feeds
                      navigationService={navigationService}
                      selectedUserLanguage={initialUserData.language}
                      selectedOpco={initialUserData.opco}
                      isReady={translationsReady}
                    />
                  }
                />
                <Route path='/' element={<Navigate replace to='/read-overview' />} />
              </Routes>
            </Suspense>
          </ThemeProvider>
        </MyListProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

const App: React.FC<AppProps> = ({ navigationService }) => {
  return (
    <ThemeContextProvider>
      <AppContent navigationService={navigationService} />
    </ThemeContextProvider>
  );
};

export default App;
